package com.tovtalk.ui.screen.post

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.tovtalk.data.local.PostDetailCache
import com.tovtalk.di.koinInject
import com.tovtalk.di.disposableKoinInject
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.KosherLevel
import com.tovtalk.domain.repository.AppRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.post.PostDetailViewModel
import com.tovtalk.ui.components.CommentsBottomSheet
import com.tovtalk.ui.components.MediaAspectRatio
import com.tovtalk.ui.components.ReelOverlay
import com.tovtalk.presentation.viewmodel.auth.AuthSheetController
import com.tovtalk.ui.components.ReportReelDialog
import com.tovtalk.ui.components.ShareBottomSheet
import com.tovtalk.data.local.TokenManager
import com.tovtalk.ui.components.TovTalkMediaFeed
import com.tovtalk.ui.LocalAppInForeground
import com.tovtalk.ui.components.TovTalkReelPlayer
import com.tovtalk.ui.components.TovTalkResourceNotFoundState
import com.tovtalk.utils.deeplink.DeepLinkLogger
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.deeplink.DeepLinkBuilder
import com.tovtalk.utils.format.formatCount
import com.tovtalk.utils.logger.Logger
import com.tovtalk.utils.strings.StringKeys
import com.tovtalk.utils.strings.StringProvider
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.ui.graphics.Color
import com.tovtalk.ui.adaptive.LocalWindowSizeClass
import com.tovtalk.ui.adaptive.WindowSizeClass
import com.tovtalk.ui.components.CommentsPanel

private fun isAuthRelatedError(message: String?): Boolean {
    if (message.isNullOrBlank()) return false
    val m = message.lowercase()
    return "unauthorized" in m || "401" in m || "auth" in m || "login" in m || "token" in m
}

@Composable
fun PostDetailScreen(
    postRef: String,
    onNavigateBack: () -> Unit,
    onNavigateToProfile: (Int) -> Unit,
    onNavigateToHome: (() -> Unit)? = null,
    onNavigateToLogin: (() -> Unit)? = null,
    onNavigateToSettings: (() -> Unit)? = null,
    onNavigateToStories: ((Int) -> Unit)? = null,
    viewModel: PostDetailViewModel = disposableKoinInject()
) {
    val windowSizeClass = LocalWindowSizeClass.current
    val postState by viewModel.postState.collectAsState()
    val currentUserIsReadOnly by viewModel.currentUserIsReadOnly.collectAsState()
    var isPlaying by remember { mutableStateOf(true) }
    var showShareSheet by remember { mutableStateOf(false) }
    var showCommentsSheet by remember { mutableStateOf(false) }
    var showReportDialog by remember { mutableStateOf(false) }
    val authSheetController: AuthSheetController = koinInject()
    val authState by TokenManager.authStateFlow.collectAsState()
    val isGuestMode = authState.isGuestMode
    val inForeground = LocalAppInForeground.current
    val userRepository: UserRepository = koinInject()
    val cachedUser by userRepository.getCachedUser().collectAsState(initial = null)
    val appRepository: AppRepository = koinInject()
    var kosherLevels by remember { mutableStateOf<List<KosherLevel>>(emptyList()) }
    LaunchedEffect(Unit) {
        when (val result = appRepository.getKosherLevels()) {
            is DomainResult.Success -> kosherLevels = result.data
            else -> { /* keep default empty — commentsEnabled stays true */ }
        }
    }
    val commentsEnabled by remember(kosherLevels, cachedUser) {
        derivedStateOf {
            kosherLevels.find { it.id == cachedUser?.kashrutLevel }?.allowTextComments ?: true
        }
    }

    LaunchedEffect(inForeground) {
        if (!inForeground) isPlaying = false
    }

    LaunchedEffect(postRef) {
        val initialPost = PostDetailCache.getAndRemove(postRef)
        viewModel.loadPost(postRef, initialPost)
    }

    LaunchedEffect(postState) {
        when (val s = postState) {
            is UiState.Success -> {
                DeepLinkLogger.clearIfMatchesPost(postRef, arrived = true)
                val post = s.data
                val videoUrl = post.videoUrl
                if (videoUrl.isNotBlank()) {
                    Logger.d("TT_VIDEO: entered_video_view | source=post_detail | postRef=$postRef", "TT_VIDEO")
                }
            }
            is UiState.Error -> DeepLinkLogger.clearIfMatchesPost(postRef, arrived = false)
            else -> {}
        }
    }

    Box(modifier = Modifier.fillMaxSize().background(TovTalkTheme.colors.videoBackground)) {
        when (val state = postState) {
            is UiState.Loading -> {
                CircularProgressIndicator(
                    modifier = Modifier.align(Alignment.Center),
                    color = TovTalkTheme.colors.onBrand
                )
            }
            is UiState.Error -> {
                val isAuthError = isAuthRelatedError(state.message)
                val navigateToLogin = onNavigateToLogin
                if (navigateToLogin != null && isAuthError) {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .align(Alignment.Center)
                    ) {
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(TovTalkTheme.colors.videoBackground.copy(alpha = TovTalkTheme.opacity.intense)),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.md),
                                modifier = Modifier.padding(TovTalkTheme.spacing.xl)
                            ) {
                                Text(
                                    text = StringProvider.get("video_login_to_watch"),
                                    style = TovTalkTheme.typography.titleLarge,
                                    color = TovTalkTheme.colors.onBrand
                                )
                                Text(
                                    text = StringProvider.get("video_login_to_watch_subtitle"),
                                    style = TovTalkTheme.typography.bodyMedium,
                                    color = TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.opaque)
                                )
                                Button(
                                    onClick = navigateToLogin,
                                    modifier = Modifier.padding(top = TovTalkTheme.spacing.xs),
                                    colors = androidx.compose.material3.ButtonDefaults.buttonColors(
                                        containerColor = TovTalkTheme.colors.brand,
                                        contentColor = TovTalkTheme.colors.onBrand
                                    )
                                ) {
                                    Text(StringProvider.get("auth_login_button"))
                                }
                            }
                        }
                    }
                } else {
                    val goHome = onNavigateToHome
                    if (goHome != null) {
                        TovTalkResourceNotFoundState(
                            modifier = Modifier.align(Alignment.Center),
                            onNavigateToHome = goHome
                        )
                    } else {
                        Column(
                            modifier = Modifier.align(Alignment.Center),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Text(state.message, color = TovTalkTheme.colors.onBrand)
                            Button(onClick = { viewModel.loadPost(postRef) }) {
                                Text(StringProvider.get("common_retry"))
                            }
                        }
                    }
                }
            }
            is UiState.KashrutBlocked -> {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(TovTalkTheme.spacing.xl)
                        .align(Alignment.Center),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.md)
                ) {
                    Text(
                        text = state.message,
                        style = TovTalkTheme.typography.bodyLarge,
                        color = TovTalkTheme.colors.onBrand,
                        textAlign = androidx.compose.ui.text.style.TextAlign.Center
                    )
                    if (currentUserIsReadOnly) {
                        onNavigateToSettings?.let { goToSettings ->
                            Button(
                                onClick = goToSettings,
                                colors = androidx.compose.material3.ButtonDefaults.buttonColors(
                                    containerColor = TovTalkTheme.colors.brand,
                                    contentColor = TovTalkTheme.colors.onBrand
                                )
                            ) {
                                Text(StringProvider.get(StringKeys.KASHRUT_GO_TO_SETTINGS))
                            }
                        }
                    }
                }
            }
            is UiState.Success -> {
                val post = state.data
                if (showShareSheet) {
                    ShareBottomSheet(
                        deepLinkUrl = DeepLinkBuilder.buildPostUrl(post.ref),
                        onDismiss = { showShareSheet = false }
                    )
                }
                if (showCommentsSheet) {
                    CommentsBottomSheet(
                        postId = post.id,
                        postOwnerId = post.author.id,
                        onDismiss = { showCommentsSheet = false },
                        onNavigateToProfile = onNavigateToProfile,
                        onCommentAdded = { viewModel.incrementCommentCount(post.id) }
                    )
                }
                if (showReportDialog) {
                    ReportReelDialog(
                        postId = post.id,
                        onDismiss = { showReportDialog = false },
                        onReported = { showReportDialog = false }
                    )
                }
                if (windowSizeClass == WindowSizeClass.Expanded) {
                    Row(modifier = Modifier.fillMaxSize().background(Color.Black)) {
                        Box(
                            modifier = Modifier
                                .weight(1f)
                                .fillMaxSize()
                                .clickable { isPlaying = !isPlaying },
                            contentAlignment = Alignment.Center
                        ) {
                            Box(
                                modifier = Modifier
                                    .aspectRatio(9f / 16f)
                                    .fillMaxHeight()
                            ) {
                                val videoUrl = post.videoUrl
                                if (videoUrl.isNotBlank()) {
                                    TovTalkReelPlayer(
                                        url = videoUrl,
                                        isPlaying = isPlaying,
                                        thumbnailUrl = post.thumbnail,
                                        modifier = Modifier.fillMaxSize()
                                    )
                                } else {
                                    TovTalkMediaFeed(
                                        imageUrl = post.thumbnail,
                                        modifier = Modifier.fillMaxSize(),
                                        aspectRatio = MediaAspectRatio.VERTICAL_VIDEO
                                    )
                                }
                                ReelOverlay(
                                    topGradientFraction = 0.15f,
                                    bottomGradientFraction = 0.25f,
                                    isPlaying = isPlaying,
                                    showDoubleTapHeart = false,
                                    avatarUrl = post.author.profilePhoto,
                                    username = post.author.username,
                                    showFollowButton = false,
                                    onUserClick = { onNavigateToProfile(post.author.id) },
                                    onFollowClick = { },
                                    followButton = { },
                                    description = post.caption,
                                    musicTitle = post.music?.title ?: post.music?.let { StringProvider.get("video_original_sound") },
                                    viewCountText = "${formatCount(post.stats.views)} ${StringProvider.get("video_views")}",
                                    isLiked = post.viewerState.isLiked,
                                    likeCount = formatCount(post.stats.likes),
                                    commentCount = formatCount(post.stats.comments),
                                    shareCount = formatCount(post.stats.shares),
                                    onLikeClick = { viewModel.likePost(post.id, post.viewerState.isLiked) },
                                    onCommentClick = {}, // no-op: comments already visible in panel
                                    onShareClick = { showShareSheet = true },
                                    onReportClick = { if (isGuestMode) authSheetController.open(reason = "guest_prompt_report_title") else showReportDialog = true },
                                    showLeftColumnInOverlay = true,
                                    commentsEnabled = commentsEnabled,
                                    createdAt = post.createdAt,
                                    hashtags = post.hashtags,
                                    hasAuthorStory = post.author.hasActiveStory,
                                    onAuthorStoryClick = onNavigateToStories?.let { nav -> { nav(post.author.id) } },
                                )
                            }
                        }
                        CommentsPanel(postId = post.id)
                    }
                } else {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .clickable { isPlaying = !isPlaying }
                ) {
                    Box(
                        modifier = Modifier
                            .aspectRatio(9f / 16f)
                            .fillMaxWidth()
                            .align(Alignment.Center)
                    ) {
                        val videoUrl = post.videoUrl
                        if (videoUrl.isNotBlank()) {
                            TovTalkReelPlayer(
                                url = videoUrl,
                                isPlaying = isPlaying,
                                thumbnailUrl = post.thumbnail,
                                modifier = Modifier.fillMaxSize()
                            )
                        } else {
                            TovTalkMediaFeed(
                                imageUrl = post.thumbnail,
                                modifier = Modifier.fillMaxSize(),
                                aspectRatio = MediaAspectRatio.VERTICAL_VIDEO
                            )
                        }
                        ReelOverlay(
                            topGradientFraction = 0.15f,
                            bottomGradientFraction = 0.25f,
                            isPlaying = isPlaying,
                            showDoubleTapHeart = false,
                            avatarUrl = post.author.profilePhoto,
                            username = post.author.username,
                            showFollowButton = false,
                            onUserClick = { onNavigateToProfile(post.author.id) },
                            onFollowClick = { },
                            followButton = { },
                            description = post.caption,
                            musicTitle = post.music?.title ?: post.music?.let { StringProvider.get("video_original_sound") },
                            viewCountText = "${formatCount(post.stats.views)} ${StringProvider.get("video_views")}",
                            isLiked = post.viewerState.isLiked,
                            likeCount = formatCount(post.stats.likes),
                            commentCount = formatCount(post.stats.comments),
                            shareCount = formatCount(post.stats.shares),
                            onLikeClick = { viewModel.likePost(post.id, post.viewerState.isLiked) },
                            onCommentClick = { showCommentsSheet = true },
                            onShareClick = { showShareSheet = true },
                            onReportClick = { showReportDialog = true },
                            showLeftColumnInOverlay = true,
                            commentsEnabled = commentsEnabled,
                            createdAt = post.createdAt,
                            hashtags = post.hashtags,
                            hasAuthorStory = post.author.hasActiveStory,
                            onAuthorStoryClick = onNavigateToStories?.let { nav -> { nav(post.author.id) } },
                        )
                    }
                }
                }
            }
            else -> {}
        }
        IconButton(
            onClick = onNavigateBack,
            modifier = Modifier
                .align(Alignment.TopStart)
                .padding(TovTalkTheme.spacing.md)
                .statusBarsPadding()
                .background(TovTalkTheme.colors.videoBackground.copy(alpha = TovTalkTheme.opacity.subtle), CircleShape)
        ) {
            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = StringProvider.get("common_back"), tint = TovTalkTheme.colors.onBrand)
        }
    }
}
